import { useEffect, useState, useRef } from 'react';

const SLOW_NETWORK_TIMEOUT_MS = 7000;

/**
 * Tracks whether the video has loaded within the timeout.
 * Returns `true` when the timeout fires and video is still not ready.
 * Automatically resets when `isReady` becomes true or `src` changes.
 */
export function useSlowNetworkWarning(
  isReady: boolean,
  src: string | undefined,
  enabled: boolean
): { showWarning: boolean; dismiss: () => void } {
  const [showWarning, setShowWarning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Reset on src change or when disabled
    setShowWarning(false);

    if (!enabled || !src) return;

    timerRef.current = setTimeout(() => {
      // Only show if still not ready when timer fires
      setShowWarning(true);
    }, SLOW_NETWORK_TIMEOUT_MS);
    return () => {
      clearTimeout(timerRef.current);
    };
  }, [src, enabled]);

  // Hide warning once video becomes ready
  useEffect(() => {
    if (isReady) {
      clearTimeout(timerRef.current);
      setShowWarning(false);
    }
  }, [isReady]);

  const dismiss = () => setShowWarning(false);

  return { showWarning, dismiss };
}

